/**
 * authService — Supabase-shaped surface backed by FastAPI + Mongo.
 *
 * Intent: when Supabase wiring lands, only this module changes; nothing else.
 *
 * TODO (Phase ≥3): replace localStorage Bearer with httpOnly cookies once
 * Supabase replaces the in-house JWT layer. Drop manual token persistence here.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "thermocalc.session.token";

const http = axios.create({ baseURL: API });

http.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

const persist = (token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
};

export const authService = {
    async signUp({ email, password, name, company }) {
        const { data } = await http.post("/auth/signup", {
            email,
            password,
            name,
            company,
        });
        persist(data.token);
        return { account: data.account, token: data.token };
    },

    async signIn({ email, password }) {
        const { data } = await http.post("/auth/login", { email, password });
        persist(data.token);
        return { account: data.account, token: data.token };
    },

    async signOut() {
        try {
            await http.post("/auth/logout");
        } catch (_) {
            /* stateless — ignore */
        }
        persist(null);
    },

    async getSession() {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) return null;
        try {
            const { data } = await http.get("/auth/me");
            return { account: data, token };
        } catch (_) {
            persist(null);
            return null;
        }
    },

    getUser() {
        return localStorage.getItem(TOKEN_KEY);
    },

    /** Authenticated axios instance — share with feature modules. */
    http,
};

/** Map FastAPI error payloads to a readable string (422 returns array). */
export function formatApiError(err) {
    const detail = err?.response?.data?.detail;
    if (detail == null) return err?.message || "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
